"use client";

// Barra de navegación inferior para teléfono.
//
// En mobile el menú del encabezado está oculto (sm:flex), así que quien entra
// desde el celular no tenía forma de llegar a buscar, tienda ni sus
// reparaciones sin volver a la portada. El pulgar llega abajo, no arriba.
//
// Solo se muestra con sesión iniciada: a un visitante sin cuenta le tapa
// contenido de la portada sin darle nada a cambio.
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import Icon, { IconName } from "./Icon";
import { INICIO_KEY, hasSession } from "../lib/api";

// Cinco solapas y ni una más: en un teléfono de 360 px la sexta deja cada
// destino en 60 px de ancho, abajo del área de toque de 44 px cómoda y con el
// texto partido en dos renglones.
//
// Sale "Buscar" y entra "Beneficios". El motivo: /buscar es el ÚNICO destino
// que ya está a un toque desde acá — la portada abre con las pastillas
// ("Buscar técnico", "Tienda", "Para locales") y con el botón grande del hero —,
// y además no le sirve de nada al técnico, que es la mitad de la gente con
// sesión iniciada y el que paga la suscripción. Beneficios, en cambio, no tenía
// NINGUNA puerta de entrada en teléfono, y es la pantalla que trae usuarios
// nuevos (el código para invitar): esconderla es dejar la campaña sin motor.
const SOLAPAS: { href: string; icono: IconName; texto: string }[] = [
  { href: "/", icono: "home", texto: "Inicio" },
  { href: "/solicitudes", icono: "wrench", texto: "Reparaciones" },
  { href: "/tienda", icono: "cart", texto: "Tienda" },
  { href: "/beneficios", icono: "sparkles", texto: "Beneficios" },
  { href: "/perfil", icono: "user", texto: "Perfil" },
];

// El panel de administración tiene su propia navegación; encimarle otra confunde
const OCULTA_EN = ["/admin", "/ingresar", "/registro", "/recuperar", "/buscar"];

export default function NavInferior() {
  const ruta = usePathname();
  const [conSesion, setConSesion] = useState(false);
  const [inicio, setInicio] = useState("/");

  // localStorage no existe al renderizar en el servidor: se consulta al montar
  useEffect(() => {
    setConSesion(hasSession());
    // "Inicio" va directo al módulo del rol: pasando por "/" se veía un
    // parpadeo de landing antes de la redirección.
    setInicio(localStorage.getItem(INICIO_KEY) ?? "/");
  }, [ruta]);

  if (!conSesion) return null;
  if (OCULTA_EN.some((p) => ruta === p || ruta.startsWith(`${p}/`))) return null;

  return (
    <>
      {/* Reserva el alto de la barra para que no tape el pie de página */}
      <div aria-hidden className="sm:hidden h-[calc(4rem+env(safe-area-inset-bottom))]" />

      <nav
        aria-label="Navegación principal"
        className="sm:hidden fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[#14191B]/95 backdrop-blur"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="flex">
          {SOLAPAS.map((s) => {
            const destino = s.href === "/" ? inicio : s.href;
            const activa = s.href === "/" ? ruta === inicio || ruta === "/" : ruta.startsWith(s.href);
            return (
              <li key={s.href} className="flex-1">
                <Link
                  href={destino}
                  aria-current={activa ? "page" : undefined}
                  className={`relative flex flex-col items-center justify-center gap-1 h-16 text-[10.5px] font-semibold transition ${
                    activa ? "text-[#E8A23C]" : "text-[#808D88] active:text-[#B5C0BB]"
                  }`}
                >
                  {/* Barra fina arriba del icono activo: se lee de un vistazo
                      sin depender solo del color, que en pantallas al sol se pierde */}
                  <span
                    aria-hidden
                    className={`absolute top-0 h-0.5 w-10 rounded-full transition ${
                      activa ? "bg-[#E8A23C]" : "bg-transparent"
                    }`}
                  />
                  <Icon name={s.icono} size={21} strokeWidth={activa ? 2.2 : 1.75} />
                  {s.texto}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
